import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { StockService } from '../../../../../../services/stock/stock.service';

export interface ApercuRow {
    label: string;
    value: number;
}

@Component({
    selector: 'app-apercu',
    templateUrl: './apercu.component.html',
    styleUrls: ['./apercu.component.scss']
})
export class ApercuComponent implements OnInit {
    currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');

    stationId = this.currentUser?.stations?.[0]?.id;
    warehouseId!: number;
    /** Filtre optionnel par id de catégorie */
    selectedCategoryId?: number;

    apercuData: ApercuRow[] = [];
    total = 0;
    isLoading = false;

    constructor(
        private stockService: StockService,
        private route: ActivatedRoute
    ) {}

    ngOnInit(): void {
        this.route.queryParamMap.subscribe((q) => {
            this.warehouseId = Number(q.get('entrepot_id')) || 0;
            this.loadApercu();
        });
    }

    /**
     * GET /api/stock/entrepot/:entrepot_id — une ligne par produit : label = nom, valeur = qté × prix d’achat.
     */
    loadApercu(): void {
        if (!this.warehouseId) {
            this.isLoading = false;
            this.apercuData = [];
            this.total = 0;
            return;
        }

        this.isLoading = true;
        const accumulated: any[] = [];
        let apiPage = 1;
        const pageSize = 500;

        const fetchPage = (): void => {
            this.stockService.getStockByEntrepot(this.warehouseId, apiPage, pageSize).subscribe({
                next: (res) => {
                    const stocks = res.data?.stocks ?? [];
                    accumulated.push(...stocks);
                    const lastPage =
                        res.data?.pagination?.last_page ??
                        res.data?.pagination?.total_pages ??
                        res.data?.pagination?.lastPage;
                    const hasMore =
                        stocks.length >= pageSize &&
                        (lastPage == null || apiPage < Number(lastPage));

                    if (hasMore && stocks.length > 0) {
                        apiPage++;
                        fetchPage();
                    } else {
                        this.processStockLines(accumulated);
                    }
                },
                error: () => {
                    this.isLoading = false;
                    this.apercuData = [];
                    this.total = 0;
                }
            });
        };

        fetchPage();
    }

    /** Valeur = quantity × prix d’achat ; libellé affiché = nom du produit. */
    private mapStockLineMeta(stock: any): {
        productId: number;
        catId: number | null;
        valeur: number;
        nom: string;
    } {
        const p = stock?.product ?? {};
        const qty = Number(stock?.quantity ?? 0);
        const prixAchat = Number(p.prix_unitaires ?? p.prix_achat ?? stock.prix_unitaires ?? 0);
        const valeur = qty * (Number.isFinite(prixAchat) ? prixAchat : 0);
        const catId =
            p.categorie?.id != null
                ? Number(p.categorie.id)
                : p.categories_id != null
                  ? Number(p.categories_id)
                  : null;
        const productId = Number(p.product_id ?? p.id ?? stock.product_id ?? 0);
        const nom = String(p.nom ?? '').trim() || `Produit ${productId || ''}`;

        return { productId, catId, valeur, nom };
    }

    /** Une ligne par produit : `label` = nom ; fusion des lignes si même produit. */
    private rowsByProductName(mapped: Array<{ productId: number; nom: string; valeur: number }>): ApercuRow[] {
        const byKey = new Map<string, number>();
        const labelByKey = new Map<string, string>();

        for (const m of mapped) {
            const key = m.productId > 0 ? `id:${m.productId}` : `nom:${m.nom}`;
            byKey.set(key, (byKey.get(key) ?? 0) + m.valeur);
            labelByKey.set(key, m.nom);
        }

        const rows: ApercuRow[] = [];
        byKey.forEach((value, key) => {
            rows.push({
                label: labelByKey.get(key) ?? '',
                value
            });
        });
        return rows;
    }

    private processStockLines(stockLines: any[]): void {
        let mapped = stockLines.map((s) => this.mapStockLineMeta(s));

        if (this.selectedCategoryId != null) {
            const sid = Number(this.selectedCategoryId);
            mapped = mapped.filter((m) => m.catId === sid);
        }

        this.apercuData = this.rowsByProductName(mapped).map((r) => ({
            label: r.label,
            value: this.roundMoney(r.value)
        }));
        this.apercuData.sort((a, b) => a.label.localeCompare(b.label, 'fr'));

        this.total = this.roundMoney(
            this.apercuData.reduce((s, r) => s + Number(r.value ?? 0), 0)
        );
        this.isLoading = false;
    }

    private roundMoney(n: number): number {
        return Math.round(n * 1000) / 1000;
    }

    onCategoryChange(categoryId?: number): void {
        this.selectedCategoryId = categoryId;
        this.loadApercu();
    }
}
